import { useEffect, useState, useCallback, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { setTeamStructure } from './../../redux/usersSlice';
import { mergeSort } from 'utils/helpers';
import { fetchTeamStructure } from 'api';

/**
 * Custom hook for fetching and caching team structure
 * Automatically uses Redux cache if available
 */

export const useTeamStructure = () => {
	const dispatch = useDispatch();
	const cachedTeam = useSelector((state) => state.users.team);

	// Local state
	const [teamData, setTeamData] = useState(cachedTeam ?? []);
	const [isLoading, setIsLoading] = useState(false);
	const [isFetching, setIsFetching] = useState(false);
	const [isError, setIsError] = useState(false);
	const [error, setError] = useState(null);

	const shouldFetch = !cachedTeam?.length;

	/* ================================
	   Fetch logic
	================================ */
	const loadTeam = useCallback(
		async (background = false) => {
			try {
				background ? setIsFetching(true) : setIsLoading(true);
				setIsError(false);
				setError(null);

				const result = await fetchTeamStructure();

				setTeamData(result?.data?.data || []);
				dispatch(setTeamStructure(result?.data?.data || []));
			} catch (err) {
				setIsError(true);
				setError(err);
			} finally {
				setIsLoading(false);
				setIsFetching(false);
			}
		},
		[dispatch],
	);

	/* ================================
	   Initial fetch
	================================ */
	useEffect(() => {
		if (shouldFetch) {
			loadTeam(false);
		}
	}, [shouldFetch, loadTeam]);

	/* ================================
	   Manual refresh (safe)
	================================ */
	const refreshTeam = useCallback(() => {
		loadTeam(true); // background refresh
	}, [loadTeam]);

	/* ================================
	   Derived data
	================================ */
	const allAgents = useMemo(
		() =>
			teamData?.flatMap(
				(manager) =>
					manager?.teamLeaders?.flatMap((tl) => tl.agents ?? []) ?? [],
			) ?? [],
		[teamData],
	);

	const allTeamLeaders = useMemo(
		() => teamData?.flatMap((manager) => manager?.teamLeaders ?? []) ?? [],
		[teamData],
	);

	const teamIndex = useMemo(() => {
		const byManagerTeamLead = new Map();
		const byManager = new Map();
		const teamLeadsByManager = new Map();
		const byTeamLead = new Map();

		for (const manager of teamData ?? []) {
			const collectedAgents = [];
			const teamLeads = manager.teamLeaders ?? [];

			for (const tl of teamLeads) {
				const agents = mergeSort(tl.agents ?? []);
				byManagerTeamLead.set(`${manager._id}:${tl._id}`, agents);

				byTeamLead.set(tl._id, agents);
				collectedAgents.push(...agents);
			}

			byManager.set(manager._id, mergeSort(collectedAgents));
			teamLeadsByManager.set(manager._id, teamLeads);
		}

		return { byManagerTeamLead, byManager, byTeamLead, teamLeadsByManager };
	}, [teamData]);

	/* ================================
	   Selectors
	================================ */
	const findManagerByTL = (tlId, teamData) => {
		for (const manager of teamData) {
			if (manager.teamLeaders?.some((tl) => tl._id === tlId)) {
				return manager._id;
			}
		}
		return null; // TL not found
	};

	const getAgentsByManagerAndTL = useCallback(
		(managerId, teamLeadId) =>
			teamIndex.byManagerTeamLead.get(`${managerId}:${teamLeadId}`) ?? [],
		[teamIndex],
	);

	const getAgentsByManager = useCallback(
		(managerId) => teamIndex.byManager.get(managerId) ?? [],
		[teamIndex],
	);

	const getTeamLeadsByManager = useCallback(
		(managerId) => teamIndex.teamLeadsByManager.get(managerId) ?? [],
		[teamIndex],
	);

	const getAgentsByTL = useCallback(
		(tlId) => teamIndex?.byTeamLead?.get(tlId) ?? [],
		[teamIndex],
	);

	/* ================================
	   Public API
	================================ */
	return useMemo(
		() => ({
			team: teamData,
			allAgents,
			allTeamLeaders,
			getTeamLeadsByManager,
			findManagerByTL,
			getAgentsByManagerAndTL,
			getAgentsByManager,
			getAgentsByTL,
			isLoading,
			isFetching,
			isError,
			error,
			refreshTeam,
		}),
		[
			teamData,
			allAgents,
			allTeamLeaders,
			getTeamLeadsByManager,
			findManagerByTL,
			getAgentsByManagerAndTL,
			getAgentsByManager,
			getAgentsByTL,
			isLoading,
			isFetching,
			isError,
			error,
			refreshTeam,
		],
	);
};
